import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCcw, Download, Mail, Trash2, Search, Plus, Pencil } from 'lucide-react';
import { formatDateShort } from '../../utils/dateFormat';
import { logger } from '../../utils/logger';
import { ManualRefundModal } from '../../components/admin/ManualRefundModal';
import { EditRefundModal } from '../../components/admin/EditRefundModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const RefundsList = () => {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [emailingId, setEmailingId] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [editingRefund, setEditingRefund] = useState(null);
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');
  const headers = { 'Authorization': `Basic ${btoa(':' + password)}` };

  useEffect(() => {
    if (!password) { navigate('/admin/login'); return; }
    fetchRefunds();
  }, []);

  const fetchRefunds = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/refunds`, { headers });
      if (res.ok) setRefunds(await res.json());
      else if (res.status === 401) navigate('/admin/login');
    } catch (err) {
      logger.error('Failed to fetch refunds:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this refund? Revenue analytics will be recalculated.')) return;
    const res = await fetch(`${API_URL}/api/admin/refunds/${id}`, { method: 'DELETE', headers });
    if (res.ok) fetchRefunds();
    else alert('Failed to delete refund.');
  };

  const handleDownloadPDF = async (refund) => {
    const res = await fetch(`${API_URL}/api/admin/refunds/${refund.id}/pdf`, { headers });
    if (!res.ok) { alert('Failed to download PDF'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Refund_${refund.client_name.replace(/\s/g, '_')}_${refund.refund_number}.pdf`;
    document.body.appendChild(a); a.click();
    URL.revokeObjectURL(url); document.body.removeChild(a);
  };

  const handleResendEmail = async (refund) => {
    if (!window.confirm(`Re-send refund receipt to ${refund.client_email}?`)) return;
    setEmailingId(refund.id);
    try {
      const res = await fetch(`${API_URL}/api/admin/refunds/${refund.id}/send-email`, { method: 'POST', headers });
      if (res.ok) alert(`Refund receipt sent to ${refund.client_email}.`);
      else {
        const e = await res.json().catch(() => ({}));
        alert(`Failed: ${e.detail || 'Unknown error'}`);
      }
    } finally {
      setEmailingId(null);
    }
  };

  const filtered = refunds.filter(r =>
    !searchTerm ||
    r.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.refund_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.source_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const total = filtered.reduce((s, r) => s + (r.amount || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-red-600">Refunds</h1>
            <p className="text-sm text-foreground/60">Audit log of every refund issued</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setManualOpen(true)}
              data-testid="open-manual-refund-btn"
              className="px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
              title="Record a refund for a payment that pre-dates the app"
            >
              <Plus className="w-4 h-4" />
              Manual Refund
            </button>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-gray-50"
            >
              ← Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-5 rounded-xl border border-border shadow-sm">
            <div className="text-xs uppercase tracking-wider text-foreground/60 mb-1">Total refunds</div>
            <div className="text-2xl font-bold text-red-600">{refunds.length}</div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-border shadow-sm">
            <div className="text-xs uppercase tracking-wider text-foreground/60 mb-1">Total amount refunded</div>
            <div className="text-2xl font-bold text-red-600">CI${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-border shadow-sm">
            <div className="text-xs uppercase tracking-wider text-foreground/60 mb-1">Issue a new refund</div>
            <p className="text-xs text-foreground/60">From the View page of any receipt, paid invoice, or quote — or tap <strong>Manual Refund</strong> for legacy payments.</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-border shadow-sm">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground/40" />
            <input
              type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by client, refund #, or source #…"
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              data-testid="refunds-search"
            />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
          {filtered.length === 0 ? (
            <div className="text-center py-12">
              <RefreshCcw className="w-16 h-16 text-foreground/20 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No refunds yet</h3>
              <p className="text-foreground/60 text-sm">
                Issue refunds from the View page of any receipt, paid invoice, or quote.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-border">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Refund #</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Source</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Reason</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-foreground/60 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-mono text-sm font-semibold text-red-600">{r.refund_number}</td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="font-medium">{r.source_number}</div>
                          <div className="text-xs text-foreground/50 uppercase tracking-wider">{r.source_type.replace('_', ' ')}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium">{r.client_name}</div>
                        <div className="text-xs text-foreground/60">{r.client_email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{formatDateShort(r.refund_date)}</td>
                      <td className="px-6 py-4 whitespace-nowrap font-semibold text-red-700">
                        −{r.currency}{(r.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 max-w-xs">
                        <p className="text-sm text-foreground/70 truncate" title={r.reason}>{r.reason}</p>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => setEditingRefund(r)} title="Edit refund"
                                  data-testid={`refund-edit-btn-${r.id}`}
                                  className="p-2 text-foreground/60 hover:text-amber-600 hover:bg-amber-50 rounded-lg">
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDownloadPDF(r)} title="Download PDF"
                                  className="p-2 text-foreground/60 hover:text-green-600 hover:bg-green-50 rounded-lg">
                            <Download className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleResendEmail(r)} disabled={emailingId === r.id} title="Resend email"
                                  className="p-2 text-foreground/60 hover:text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50">
                            <Mail className={`w-4 h-4 ${emailingId === r.id ? 'animate-pulse' : ''}`} />
                          </button>
                          <button onClick={() => handleDelete(r.id)} title="Delete"
                                  data-testid={`refund-delete-btn-${r.id}`}
                                  className="p-2 text-foreground/60 hover:text-red-600 hover:bg-red-50 rounded-lg">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ManualRefundModal
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        onIssued={(refund) => {
          fetchRefunds();
          alert(`Manual refund ${refund.refund_number} recorded for ${refund.client_name}.`);
        }}
      />

      <EditRefundModal
        refund={editingRefund}
        onClose={() => setEditingRefund(null)}
        onSaved={(updated) => {
          fetchRefunds();
          alert(`Refund ${updated.refund_number} updated.`);
        }}
      />
    </div>
  );
};

export default RefundsList;
